<template>
  <div
      class="modal fade"
      :class="{ show: visible }"
      tabindex="-1"
      role="dialog"
      :style="{ display: visible ? 'block' : 'none' }"
      :data-modal-name="modalName || null"
      @click.self="onCancel"
      @keyup.esc="onCancel"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <!-- Заголовок -->
        <div class="modal-header">
          <slot name="header">
            <h5 class="modal-title">{{ title }}</h5>
          </slot>
          <button type="button" class="btn-close" aria-label="Close" @click="onCancel"></button>
        </div>

        <!-- Основной контент -->
        <div class="modal-body">
          <slot>
            <!-- сюда попадёт содержимое, если слот не переопределён -->
          </slot>
        </div>

        <!-- Подвал -->
        <div class="modal-footer">
          <slot name="actions">
            <button type="button" class="btn btn-secondary" @click="onCancel">Отмена</button>
            <button type="button" class="btn btn-danger" @click="onOk">OK</button>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, defineEmits, onBeforeUnmount, watch } from 'vue';

interface Props {
  modelValue: boolean;
  title?: string;
  closeOnEsc?: boolean;      // default true
  closeOnBackdrop?: boolean; // default true
  modalName?: string;        // имя модалки для e2e-тестов (data-modal-name)
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'ok'): void;
  (e: 'cancel'): void;
}>();

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
});

const onOk = () => {
  emit('ok');
  visible.value = false;
};

const onCancel = () => {
  emit('cancel');
  visible.value = false;
};

// ESC-закрытие
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.closeOnEsc !== false) {
    onCancel();
  }
};

// Auto-focus при открытии модалки
const setAutoFocus = () => {
  // Находим текущую модалку по modalName или по классу
  const currentModal = document.querySelector(`[data-modal-name="${props.modalName || ''}"]`) || 
                      document.querySelector('.modal.show');
  
  if (!currentModal) return;
  
  // Ищем все элементы, которые могут получить фокус ТОЛЬКО внутри модалки
  // Исключаем кнопки в footer, так как они будут обработаны отдельно в fallback
  const focusableSelectors = [
    '.modal-body button:not([disabled])',
    '.modal-body input:not([disabled])',
    '.modal-body select:not([disabled])',
    '.modal-body textarea:not([disabled])',
    '.modal-body a[href]',
    '.modal-body [tabindex]:not([tabindex="-1"])'
  ].join(', ');
  
  const focusableElements = currentModal.querySelectorAll(focusableSelectors);
  
  if (focusableElements.length === 0) {
    // Если нет фокусируемых элементов, ищем кнопки в footer (actions)
    // Учитываем LoadingButton wrapper - ищем кнопки внутри .modal-footer
    const actionButtons = currentModal.querySelectorAll('.modal-footer button:not([disabled])');
    if (actionButtons.length > 0) {
      // Фокусируемся на последнюю активную кнопку в actions
      (actionButtons[actionButtons.length - 1] as HTMLElement).focus();
    } else {
      // Если нет кнопок в actions, фокусируемся на кнопку закрытия
      const closeButton = currentModal.querySelector('.modal-header .btn-close') as HTMLElement;
      if (closeButton) {
        closeButton.focus();
      }
    }
    return;
  }
  
  // Ищем элементы с data-autofocus
  const autofocusElements = Array.from(focusableElements).filter(el => 
    el.hasAttribute('data-autofocus')
  );
  
  if (autofocusElements.length > 0) {
    // Фокусируемся на первый элемент с data-autofocus
    (autofocusElements[0] as HTMLElement).focus();
  } else {
    // Фокусируемся на первый доступный элемент
    (focusableElements[0] as HTMLElement).focus();
  }
};

watch(
    () => visible.value,
    (val) => {
      if (val) {
        document.body.classList.add('modal-open');
        document.addEventListener('keydown', onKeydown);
        // Устанавливаем auto-focus после следующего тика, чтобы DOM успел обновиться
        setTimeout(setAutoFocus, 0);
      } else {
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', onKeydown);
      }
    },
    { immediate: true }
);

onBeforeUnmount(() => {
  document.body.classList.remove('modal-open');
  document.removeEventListener('keydown', onKeydown);
});
</script>

<style scoped>
/*
  Берём класс Bootstrap-Modal, но «подсвечиваем фон»:
  1) background-color: rgba(0,0,0,0.5) — полупрозрачный чёрный фон
  2) backdrop-filter: blur(2px) — лёгкое размытие (можно отключить, если не нужно)
  3) z-index: 1050 — соответствует базовому z-index дропдаунов и прочего
*/
.modal {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 1050;
}

/* Когда модалка активна, добавляется класс .show */
/* display: block появляется через inline-стиль в шаблоне */

/* Собственно контент модалки (Bootstrap-стандарт) */
/* Здесь можно ничего не менять — Bootstrap сам выставит нужные размеры */
/* .modal-content, .modal-header, .modal-body, .modal-footer — всё подтягивается из Bootstrap CSS */

/* Чтобы при открытии не скроллировалась основная страница */
body.modal-open {
  overflow: hidden;
}
</style>
